/**
 * Fase 6 — Privacy & gegevens sectie.
 * Zelfstandige component die aan Settings.jsx onderaan gehangen wordt.
 * Bevat alle GDPR-acties (personal export, account delete, company export,
 * company delete) met de bijhorende bevestigingsmodals.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { Download, Trash2, ShieldAlert, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

async function downloadZip(url, filename, t) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/zip" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  } catch (e) {
    const detail = e.response?.data?.detail;
    const code = detail?.code || detail;
    toast.error(t(`err_${code}`) || formatApiErrorDetail(detail));
    return false;
  }
  return true;
}

export default function GdprSection() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [company, setCompany] = useState(null);
  const [showAcct, setShowAcct] = useState(false);
  const [showCo, setShowCo] = useState(false);
  const isOwner = user?.role === "owner";

  useEffect(() => {
    (async () => {
      try { setCompany((await api.get("/companies/me")).data); } catch {}
    })();
  }, []);

  const dlMe = () => downloadZip("/gdpr/export/me.zip", `rostr-my-data.zip`, t);
  const dlCo = () => downloadZip("/gdpr/export/company.zip", `rostr-company.zip`, t);

  const cancelCoDelete = async () => {
    try {
      await api.post("/gdpr/company/delete/cancel");
      toast.success(t("company_deletion_cancelled"));
      setCompany((await api.get("/companies/me")).data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const pending = company?.deletion_state === "pending";
  const scheduled = company?.deletion_scheduled_at;

  return (
    <>
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="card p-6 md:p-8" data-testid="section-gdpr"
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center shrink-0">
            <ShieldAlert size={16} strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-display text-[18px] font-bold text-[hsl(var(--text-strong))]">{t("privacy_and_data")}</div>
            <div className="text-[13px] text-[hsl(var(--text-muted))] mt-0.5">{t("privacy_and_data_desc")}</div>
          </div>
        </div>

        {/* Personal section — every role */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
          <ActionRow
            testid="gdpr-download-me"
            icon={Download} tone="primary"
            title={t("download_my_data")} desc={t("download_my_data_desc")}
            action={t("download_my_data")} onClick={dlMe}
          />
          <ActionRow
            testid="gdpr-delete-me"
            icon={Trash2} tone="danger"
            title={t("delete_account")} desc={t("delete_account_desc")}
            action={t("delete_account")} onClick={() => setShowAcct(true)}
          />
        </div>

        {/* Owner-only danger zone */}
        {isOwner && (
          <>
            <div className="mt-8 mb-3 flex items-center gap-2 eyebrow text-[hsl(var(--danger))]">
              <AlertTriangle size={12} strokeWidth={2} /> {t("danger_zone")}
            </div>
            {pending && (
              <div
                data-testid="gdpr-pending-banner"
                className="mb-4 p-4 rounded-2xl border hairline bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] flex items-center justify-between gap-3"
              >
                <div className="text-[13px] font-semibold">
                  {t("company_deletion_scheduled").replace("{date}", new Date(scheduled).toLocaleDateString())}
                </div>
                <button
                  data-testid="gdpr-cancel-co-delete"
                  onClick={cancelCoDelete}
                  className="btn btn-ghost !h-9"
                >{t("cancel_company_deletion")}</button>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ActionRow
                testid="gdpr-export-co"
                icon={Download} tone="primary"
                title={t("export_company")} desc={t("export_company_desc")}
                action={t("export_company")} onClick={dlCo}
              />
              <ActionRow
                testid="gdpr-delete-co"
                icon={Trash2} tone="danger"
                title={t("delete_company")} desc={t("delete_company_desc")}
                action={t("delete_company")} onClick={() => setShowCo(true)}
                disabled={pending}
              />
            </div>
          </>
        )}
      </motion.section>

      {showAcct && (
        <AccountDeleteModal
          t={t} user={user}
          onClose={() => setShowAcct(false)}
          onDone={() => { toast.success(t("account_deleted_ok")); logout(); }}
        />
      )}
      {showCo && (
        <CompanyDeleteModal
          t={t} company={company}
          onClose={() => setShowCo(false)}
          onDone={async () => {
            setShowCo(false);
            setCompany((await api.get("/companies/me")).data);
          }}
        />
      )}
    </>
  );
}

function ActionRow({ icon: Icon, tone, title, desc, action, onClick, disabled, testid }) {
  const isDanger = tone === "danger";
  return (
    <div className="rounded-2xl border hairline p-4 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0
          ${isDanger ? "bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))]" : "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]"}`}>
          <Icon size={15} strokeWidth={1.9} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-bold text-[hsl(var(--text-strong))]">{title}</div>
          <div className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5 leading-snug">{desc}</div>
        </div>
      </div>
      <button
        data-testid={testid}
        onClick={onClick}
        disabled={disabled}
        className={`self-end btn !h-9 ${isDanger ? "btn-ghost text-[hsl(var(--danger))]" : "btn-primary"}`}
      >
        {action}
      </button>
    </div>
  );
}

function AccountDeleteModal({ t, user, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const needsPw = !!user?.email && !user.email.endsWith("@rostr.local");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/gdpr/account/delete", needsPw ? { password } : {});
      onDone();
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      toast.error(t(`err_${code}`) || formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} testid="account-delete-modal" title={t("delete_account")}>
      <p className="text-[13px] text-[hsl(var(--text-muted))] mb-4">{t("delete_account_desc")}</p>
      <form onSubmit={submit} className="space-y-3">
        {needsPw && (
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">{t("current_password")}</span>
            <input
              type="password" required autoFocus
              data-testid="acct-delete-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="input-field"
            />
          </label>
        )}
        <button data-testid="acct-delete-confirm" disabled={busy}
                className="btn !h-10 w-full !bg-[hsl(var(--danger))] !text-white !border-transparent">
          {t("confirm_delete")}
        </button>
      </form>
    </Modal>
  );
}

function CompanyDeleteModal({ t, company, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/gdpr/company/delete", { password, confirm_name: confirmName });
      toast.success(t("company_deletion_scheduled").replace("{date}", new Date(res.data.scheduled_at).toLocaleDateString()));
      onDone();
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      toast.error(t(`err_${code}`) || formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} testid="company-delete-modal" title={t("delete_company")}>
      <p className="text-[13px] text-[hsl(var(--text-muted))] mb-3">{t("grace_period_info")}</p>
      <form onSubmit={submit} className="space-y-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">
            {t("type_company_name_to_confirm")} <span className="font-bold text-[hsl(var(--text-strong))]">— {company?.name}</span>
          </span>
          <input
            required autoFocus data-testid="co-delete-name"
            value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
            className="input-field"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">{t("current_password")}</span>
          <input
            type="password" required data-testid="co-delete-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="input-field"
          />
        </label>
        <button data-testid="co-delete-confirm" disabled={busy}
                className="btn !h-10 w-full !bg-[hsl(var(--danger))] !text-white !border-transparent">
          {t("confirm_delete")}
        </button>
      </form>
    </Modal>
  );
}

function Modal({ children, title, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
         onClick={onClose} data-testid={testid}>
      <div className="card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-[17px] font-bold text-[hsl(var(--text-strong))]">{title}</div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))]"
                  aria-label={"close"}><X size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
